/**
 * Operator marks on the Segnali lanes: a colour on a card, and a hand-made
 * order. Both are *view* preferences — nothing here reaches the bot, the signal
 * stream or panel.db — so they live in localStorage next to the alert and
 * auto-open switches: per browser, per operator, never synced.
 *
 * The palette is deliberately cool. Green, amber and red are the console's own
 * state channel — the tap tones, the amber rail while a review is open, the red
 * rail once a delete is armed — and a mark that borrowed one of them would be a
 * mark that lies the moment a VAR lands. Same reason the colour paints the
 * NAMEPLATE and the card wash but never the left rail: the rail stays the
 * system's to speak with.
 *
 * `order` holds EVERY match the panel knows, not just the ones that were
 * dragged, and not just the ones on screen. Two reasons, both bugs that were
 * live before it did:
 *
 *   - the server's own list order is `updated_at DESC` (db.listGroups), and
 *     `updated_at` moves on its own — every automatic market pre-check rewrites
 *     it for a whole batch of groups. Anything not pinned here would drift
 *     around the grid by itself between one pre-check and the next.
 *   - a drag used to store the visible list, so a match switched off for the
 *     evening lost its place (its colour survived, its position didn't) and came
 *     back at the bottom. Moves now splice the full order, hidden rows included.
 *
 * A match enters the order at the END the first time it is seen and leaves it
 * when the watch-list forgets it — the two rules the operator asked for, made
 * structural instead of emergent.
 */
import { useSyncExternalStore } from "react";

export type LaneTint = { id: string; label: string; hex: string };

/** Seven hues, each far enough from the next to be told apart across a grid of
 *  twelve cards without reading a single team name. */
export const LANE_TINTS: LaneTint[] = [
  { id: "cyan", label: "Ciano", hex: "#38bdf8" },
  { id: "indigo", label: "Indaco", hex: "#818cf8" },
  { id: "violet", label: "Viola", hex: "#a78bfa" },
  { id: "magenta", label: "Magenta", hex: "#e879f9" },
  { id: "pink", label: "Rosa", hex: "#f472b6" },
  { id: "lime", label: "Lime", hex: "#bef264" },
  { id: "slate", label: "Ardesia", hex: "#94a3b8" },
];

export const tintHex = (id?: string): string | undefined => LANE_TINTS.find((t) => t.id === id)?.hex;

export type LaneMarks = {
  /** match id → tint id */
  tints: Record<string, string>;
  /** every known match id, in the order the lanes read — arrival order until the
   *  operator moves something, and their order from then on */
  order: string[];
  /** the operator has actually moved a card. Without it `order` being non-empty
   *  would mean "marked", which it no longer does: it fills up on its own. */
  touched: boolean;
};

const KEY = "panel.lane-marks.v1";
const EMPTY: LaneMarks = { tints: {}, order: [], touched: false };

let marks = read();
const listeners = new Set<() => void>();

function read(): LaneMarks {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? "null");
    if (!raw || typeof raw !== "object") return EMPTY;
    // a tint id that no longer exists in the palette is dropped rather than
    // carried as a colour nothing can render
    const tints: Record<string, string> = {};
    for (const [id, tint] of Object.entries(raw.tints ?? {})) {
      if (typeof tint === "string" && LANE_TINTS.some((t) => t.id === tint)) tints[id] = tint;
    }
    const order = Array.isArray(raw.order) ? raw.order.filter((id: unknown): id is string => typeof id === "string") : [];
    // marks written before `touched` existed: a stored order was a hand-made one
    // back then, so treat it as one
    const touched = typeof raw.touched === "boolean" ? raw.touched : order.length > 0;
    return { tints, order, touched };
  } catch {
    return EMPTY;
  }
}

function write(next: LaneMarks) {
  marks = next;
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // quota or private mode: the marks still hold for this session
  }
  for (const l of listeners) l();
}

/**
 * A second tab moved a card. Without this the console's two windows each held
 * the copy they read at load, and whichever wrote next — a drag, or just
 * syncLaneMarks folding in the watch-list — put its stale copy back on top of
 * the other's work: the order silently reverted to what the OTHER tab had.
 * `storage` fires only in the tabs that did NOT write, which is exactly the set
 * that needs to catch up.
 */
if (typeof addEventListener === "function") {
  addEventListener("storage", (ev) => {
    const e = ev as StorageEvent;
    if (e.key !== null && e.key !== KEY) return; // null = localStorage.clear()
    marks = read();
    for (const l of listeners) l();
  });
}

export function useLaneMarks(): LaneMarks {
  return useSyncExternalStore(
    (onChange) => {
      listeners.add(onChange);
      return () => {
        listeners.delete(onChange);
      };
    },
    () => marks,
  );
}

export function setLaneTint(matchId: string, tint: string | null) {
  const tints = { ...marks.tints };
  if (tint) tints[matchId] = tint;
  else delete tints[matchId];
  write({ ...marks, tints });
}

/** The whole order is stored, not just the card that moved: "X before Y" only
 *  means something against a list. Always the FULL order — see the file note. */
export function setLaneOrder(order: string[]) {
  write({ ...marks, order, touched: true });
}

export function clearLaneMarks() {
  write(EMPTY);
}

/** Rows in the operator's order. Anything the order has never heard of keeps the
 *  order it arrived in, behind the rest — the render before syncLaneMarks has
 *  run, and nothing else. */
export function sortLanes<T extends { id: string }>(rows: T[], order: string[]): T[] {
  if (order.length === 0) return rows;
  const rank = new Map(order.map((id, i) => [id, i]));
  return rows
    .map((row, i) => ({ row, i }))
    .sort((a, b) => {
      const ra = rank.get(a.row.id);
      const rb = rank.get(b.row.id);
      if (ra == null && rb == null) return a.i - b.i;
      if (ra == null) return 1;
      if (rb == null) return -1;
      return ra - rb;
    })
    .map((x) => x.row);
}

/** Drop `moved` on either side of `target` within the full stored order. Hidden
 *  matches (switched off, or on another page of the console) keep their place
 *  around it: the card only ever lands next to the row it was dropped on. */
export function moveLane(order: string[], moved: string, target: string, after: boolean): string[] {
  if (moved === target) return order;
  const rest = order.filter((id) => id !== moved);
  const at = rest.indexOf(target);
  if (at < 0) return order;
  rest.splice(after ? at + 1 : at, 0, moved);
  return rest;
}

/** One place left or right among the cards actually ON SCREEN (`visible`), which
 *  is what the arrow keys promise — the stored order can hold matches between
 *  them that nobody can see, and stepping onto one of those would look like the
 *  key did nothing. */
export function nudgeLane(order: string[], visible: string[], moved: string, delta: number): string[] {
  const from = visible.indexOf(moved);
  const to = from + delta;
  if (from < 0 || to < 0 || to >= visible.length) return order;
  return moveLane(order, moved, visible[to]!, delta > 0);
}

/**
 * Fold the watch-list into the marks: matches the panel no longer knows drop out
 * (of the order AND of the colours — the list turns over every day and nothing
 * else would ever clear them), matches it has never seen before go on the end.
 *
 * `knownIds` must be EVERY group, enabled or not, in arrival order — a match
 * switched off for the evening is still known, and must not lose its place.
 */
export function syncLaneMarks(knownIds: string[]) {
  if (knownIds.length === 0) return; // an empty moment on reconnect is not "no matches"
  const known = new Set(knownIds);
  const kept = marks.order.filter((id) => known.has(id));
  const seen = new Set(kept);
  const order = [...kept, ...knownIds.filter((id) => !seen.has(id))];

  const tints: Record<string, string> = {};
  for (const [id, tint] of Object.entries(marks.tints)) if (known.has(id)) tints[id] = tint;

  const sameOrder = order.length === marks.order.length && order.every((id, i) => id === marks.order[i]);
  if (sameOrder && Object.keys(tints).length === Object.keys(marks.tints).length) return;
  write({ ...marks, tints, order });
}
